import classNames from 'classnames';
import _ from 'underscore';
import PropTypes from 'prop-types';
import React from 'react';
import { keyCode } from '@bingads-webui/keycode';
import { siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { ListSelector, AriaRoleType } from '@bingads-webui-react/list-selector';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { getPathName, getPageItems, getPageItemUrl } from './common/page-item-helper';
import { getPageMenuStyles } from './styles/page-menu-styles';
import PageMenuItem from './components/page-menu-item';

class PageMenu extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selectedFallbackPageItem: null,
      isSetLandingPageButtonFocused: false,
    };
  }

  componentDidUpdate() {
    if (this.props.perfMarker &&
      !this.props.perfMarker.isDone
    ) {
      this.props.perfMarker.done();
    }
  }

  onSelect = (selectedPageItem) => {
    const { scope, activity } = this.props;

    activity.signal(`list selector - page menu item clicked, name: ${selectedPageItem.content}`);

    if (selectedPageItem.isPageEnabled) {
      scope.save(getPathName(selectedPageItem));

      this.setState({
        selectedFallbackPageItem: null,
      });
    } else {
      this.setState(state => ({
        // Toggle the state so the callout toggles as well
        selectedFallbackPageItem: state.selectedFallbackPageItem === selectedPageItem
          ? null
          : selectedPageItem,
      }));
    }
  }

  onTabNext = (event) => {
    if (event.keyCode !== keyCode.Tab) {
      return;
    }

    const setLandingPageButton = document.querySelector('.set-landing-page-button');
    if (!setLandingPageButton) {
      return;
    }

    if (this.state.isSetLandingPageButtonFocused) {
      this.focusFirstButtonOnSubPageMenu();
      this.setState({ isSetLandingPageButtonFocused: false });
    } else {
      setLandingPageButton.focus();
      this.setState({ isSetLandingPageButtonFocused: true });
    }
  }

  onBlur = () => {
    this.setState({ isSetLandingPageButtonFocused: false });
  }

  focusFirstButtonOnSubPageMenu = () => {
    const firstButtonOnSubPageMenu = document.querySelector('.bingads-campaign-react-sub-page-menu button');
    if (firstButtonOnSubPageMenu) {
      firstButtonOnSubPageMenu.focus();
    }
  }

  handleFallbackCalloutClose = (id, e) => {
    // This prevents the base menu item from triggering it's onClick
    e.stopPropagation();

    this.setState((state) => {
      // Check to see if we have opened another callout
      if (state.selectedFallbackPageItem && id === state.selectedFallbackPageItem.id) {
        return {
          selectedFallbackPageItem: null,
        };
      }

      return {};
    });
  }

  renderDivider = (item) => {
    const { classes } = this.props;
    return (
      <div
        key={item.id}
        className={classNames([classes.divider, 'bingads-campaign-react-page-menu-divider'])}
      />);
  }

  render() {
    const {
      classes,
      siteMap,
      i18n,
      defaultLandingPage,
      setLandingPage,
      pageGroups,
      scope,
      collapsed,
    } = this.props;
    const pageMenuClassName = classNames([
      classes.pageMenuContainer,
      'bingads-campaign-react-page-menu',
      collapsed && classes.collapsedPageMenuContainer,
    ]);

    const isFallbackSelected = item =>
      !!this.state.selectedFallbackPageItem && this.state.selectedFallbackPageItem.id === item.id;

    const pageItems = getPageItems(pageGroups, siteMap, i18n, defaultLandingPage, isFallbackSelected);
    const selectedPageItem = _.findWhere(pageItems, { isSelected: true });

    return (
      <nav className={pageMenuClassName} aria-label={i18n.getString(_TL_('Page menu'))} >
        <ListSelector
          renderItem={
            item =>
            (<PageMenuItem
              pageItem={item}
              fallbackSiteMapId={item.id}
              fallbackTitle={item.content}
              fallbackShow={isFallbackSelected(item)}
              fallbackHandleClose={e => this.handleFallbackCalloutClose(item.id, e)}
              setLandingPage={setLandingPage}
              pageItemUrl={getPageItemUrl(item, scope)}
              renderIcon={this.props.renderItemIcon}
              collapsed={collapsed}
            />)
          }
          data={pageItems}
          renderDivider={this.renderDivider}
          selectedItem={selectedPageItem}
          extraData={this.state.selectedFallbackPageItem} // Tells the component to re-render
          onSelect={this.onSelect}
          onKeyDown={this.onTabNext}
          onBlur={this.onBlur}
          enableArrowKeysSupport
          ariaLabel={i18n.getString(_TL_('Page menu'))}
          // since we have button/a under item, use list to fix nested interactive controls issue
          ariaRole={AriaRoleType.List}
        />
      </nav>
    );
  }
}

PageMenu.defaultProps = {
  defaultLandingPage: null,
  setLandingPage: null,
  perfMarker: null,
  activity: { signal: _.noop },
  renderItemIcon: null,
  collapsed: false,
};

PageMenu.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getPageMenuStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  siteMap: siteMapPropType.isRequired,
  defaultLandingPage: PropTypes.string,
  setLandingPage: PropTypes.func,
  perfMarker: PropTypes.shape({
    done: PropTypes.func.isRequired,
    isDone: PropTypes.bool.isRequired,
  }),
  pageGroups: PropTypes.arrayOf(PropTypes.array).isRequired,
  activity: PropTypes.shape({
    signal: PropTypes.func.isRequired,
  }),
  renderItemIcon: PropTypes.func,
  collapsed: PropTypes.bool,
};

export const StyledPageMenu = withActivityContext(withI18nConsumer(withDefaultStyles(
  PageMenu,
  getPageMenuStyles
)));
